import React, { useState, useEffect } from 'react';
import { calculateResultsMetrics } from '../utils/metricsCalculator';
import '../index.css';
import {
  getHeaderStatus,
  getHeaderClass,
  getHeaderIcon,
  getPercentageClass,
  getCountClass,
  getMetricClass,
  calculateSubmissionsAverage,
  calculateResultsIssues,
  getIssuesColor
} from '../utils/statusHelpers';
import { StatusEvaluator } from '../context/StatusEvaluator';
import { LastGoodStatusStore } from '../utils/LastGoodStatusStore';

function DataSummary({ 
  data: summaryDataAll,
  resultsMetricsAll, 
  submit2PricesDataAll,
  feedConfigDataAll,
  dashboardGeneralData,
  onZeroSubsClick
}) {
  const [animatingSections, setAnimatingSections] = useState({});
  const [sectionsState, setSectionsState] = useState({
    submissions: true,
    results: false,
  });

  const summaryConfigs = [
    { entity: 'ENV', network: 'flare', title: 'Envision Flare' },
    { entity: 'ENV', network: 'songbird', title: 'Envision Songbird' },
    { entity: 'UYS', network: 'flare', title: 'UYS Flare' },
    { entity: 'UYS', network: 'songbird', title: 'UYS Songbird' },
  ];

  const toggleSection = (section) => {
    if (sectionsState[section]) {
      // Closing - start exit animation
      setAnimatingSections(prev => ({
        ...prev,
        [section]: 'closing'
      }));
      
      // Remove after animation completes
      setTimeout(() => {
        setSectionsState(prev => ({
          ...prev,
          [section]: false
        }));
        setAnimatingSections(prev => ({
          ...prev,
          [section]: null
        }));
      }, 500); // Match animation duration
    } else {
      // Opening
      setSectionsState(prev => ({
        ...prev,
        [section]: true
      }));
      setAnimatingSections(prev => ({
        ...prev,
        [section]: 'opening'
      }));
    }
  };
  
  return (
    <>
      <div className="data-summary">
        <div className="summary-boxes">
          {summaryConfigs.map(({ entity, network, title }) => {
            const entitySubmissionSummary = summaryDataAll?.[entity]?.[network];
            const entityResultsMetrics = resultsMetricsAll?.[entity]?.[network];
            const entitySubmit2Prices = submit2PricesDataAll?.[entity]?.[network];
            const entityFeedConfig = feedConfigDataAll?.[entity]?.[network];
            const latestRoundForDisplay = dashboardGeneralData?.[entity]?.[network]?.general?.latest_processed_voting_round || 'N/A';

            // Prefer precomputed dashboard status to avoid red when optional inputs are missing
            let headerStatus = (dashboardGeneralData?.[entity]?.[network]?.detailed_metrics?.overall_page_status || '')
              .toString()
              .toLowerCase();
            if (!headerStatus) {
              headerStatus = StatusEvaluator.evaluateFtsoSummaryBoxStatus(
                entitySubmissionSummary,
                entityResultsMetrics,
                entitySubmit2Prices,
                entityFeedConfig
              );
            }
            // Fallback: derive from submission_summary percentages to avoid false red
            if (!headerStatus) {
              const derived = getHeaderStatus(entitySubmissionSummary);
              if (derived) headerStatus = derived;
            }
            if (!headerStatus) {
              // Use last-known-good to avoid neutral/false red on missing inputs
              const lg = LastGoodStatusStore.get('ftso-submissions', `${entity}|${network}`);
              if (lg) headerStatus = lg;
            } else {
              // Persist last-good
              LastGoodStatusStore.set('ftso-submissions', `${entity}|${network}`, headerStatus);
            }
  
            return (
              <div key={`${entity}-${network}`} className="summary-box">
                <h2 className={getHeaderClass(headerStatus)}>
                  <div className="summary-box-title-group">
                    <span>{title}</span>
                    <span className="data-summary-latest-round">
                      ({latestRoundForDisplay})
                    </span>
                  </div>
                  <img
                    src={getHeaderIcon(headerStatus)}
                    alt="Status Icon"
                    className="header-icon"
                  />
                </h2>
                <div className="summary-content">
                  <div className="summary-section">
                    {/* Submissions Section */}
                    <div
                      className="section-header"
                      onClick={() => toggleSection('submissions')}
                    >
                      <span className="section-title">SUBMISSIONS</span>
                      <span
                        className={`section-value ${getPercentageClass(
                          calculateSubmissionsAverage(entitySubmissionSummary)
                        )}`}
                      >
                        {calculateSubmissionsAverage(entitySubmissionSummary)}%
                      </span>
                      <img
                        src="https://cdn.prod.website-files.com/64eb6710f68f6b333f8886a6/671a2db89cdd4d25e9d6855a_dropdown.svg"
                        alt="Toggle"
                        className={`dropdown-icon ${
                          sectionsState.submissions && animatingSections.submissions !== 'closing' ? 'open' : ''
                        }`}
                      />
                    </div>
  
                    {(sectionsState.submissions || animatingSections.submissions === 'closing') && (
                      <div className={`summary-grid ${animatingSections.submissions || ''}`}>
                        <span className="grid-header"></span>
                        <span className="grid-header">15min</span>
                        <span className="grid-header">2hr</span>
                        <span className="grid-header">6hr</span>
  
                        {/* submit1 row */}
                        <span className="method-label" style={{"--row-index": 0}}>submit1:</span>
                        {['15min', '2hr', '6hr'].map((timeframe, index) => (
                          <span
                            key={`submit1-${entity}-${network}-${timeframe}`}
                            className={`percentage ${getPercentageClass(
                              entitySubmissionSummary?.submit1?.[timeframe]?.percentage || 0
                            )}`}
                            style={{"--row-index": index + 1}}
                          >
                            {entitySubmissionSummary?.submit1?.[timeframe]?.percentage || 0}%
                          </span>
                        ))}
  
                        {/* submit2 row */}
                        <span className="method-label" style={{"--row-index": 4}}>submit2:</span>
                        {['15min', '2hr', '6hr'].map((timeframe, index) => (
                          <span
                            key={`submit2-${entity}-${network}-${timeframe}`}
                            className={`percentage ${getPercentageClass(
                              entitySubmissionSummary?.submit2?.[timeframe]?.percentage || 0
                            )}`}
                            style={{"--row-index": index + 5}}
                          >
                            {entitySubmissionSummary?.submit2?.[timeframe]?.percentage || 0}%
                          </span>
                        ))}
  
                        {/* submitSignatures row */}
                        <span className="method-label" style={{"--row-index": 8}}>signatures:</span>
                        {['15min', '2hr', '6hr'].map((timeframe, index) => (
                          <span
                            key={`signatures-${entity}-${network}-${timeframe}`}
                            className={`percentage ${getPercentageClass(
                              entitySubmissionSummary?.submitSignatures?.[timeframe]?.percentage || 0
                            )}`}
                            style={{"--row-index": index + 9}}
                          >
                            {entitySubmissionSummary?.submitSignatures?.[timeframe]?.percentage || 0}%
                          </span>
                        ))}
  
                        <span className="method-label" style={{"--row-index": 12}}>zero subs:</span>
                          {['15min', '2hr', '6hr'].map((timeframe, index) => {
                            const zeroCount = entitySubmissionSummary?.submit2?.[timeframe]?.zero_count || 0;
                            const hasRecords = zeroCount > 0;
                            return (
                              <span
                                key={`zero-subs-${entity}-${network}-${timeframe}`}
                                className={`percentage ${getCountClass(zeroCount)} ${hasRecords ? 'percentage--clickable' : ''}`}
                                style={{"--row-index": index + 13}}
                                onClick={hasRecords ? () => onZeroSubsClick(
                                  entity, 
                                  network, 
                                  timeframe, 
                                  entitySubmissionSummary?.submit2?.[timeframe]?.zero_rounds || []
                                ) : undefined}
                              >
                                {zeroCount}
                              </span>
                            );
                          })}
                        {/* Offenders removed */}
                        </div>
                    )}
  
                    {/* Results Section */}
                    <div
                      className="section-header"
                      onClick={() => toggleSection('results')}
                    >
                      <span className="section-title">RESULTS</span>
                      <span
                        className={`section-value ${getIssuesColor(
                          calculateResultsIssues(entityResultsMetrics)
                        )}`}
                      >
                        {calculateResultsIssues(entityResultsMetrics)} Issues
                      </span>
                      <img
                        src="https://cdn.prod.website-files.com/64eb6710f68f6b333f8886a6/671a2db89cdd4d25e9d6855a_dropdown.svg"
                        alt="Toggle"
                        className={`dropdown-icon ${
                          sectionsState.results && animatingSections.results !== 'closing' ? 'open' : ''
                        }`}
                      />
                    </div>
  
                    {(sectionsState.results || animatingSections.results === 'closing') && (
                      <div className={`summary-grid ${animatingSections.results || ''}`}>
                        <span className="grid-header"></span>
                        <span className="grid-header">15min</span>
                        <span className="grid-header">2hr</span>
                        <span className="grid-header">6hr</span>
  
                        {/* Late row */}
                        <span className="method-label" style={{"--row-index": 0}}>late:</span>
                        {['15min', '2hr', '6hr'].map((timeframe, index) => {
                          const value = entityResultsMetrics?.[timeframe]?.late || 0;
                          const total = entityResultsMetrics?.[timeframe]?.total || 1;
                          return (
                            <span
                              key={`late-${entity}-${network}-${timeframe}`}
                              className={`percentage ${getMetricClass(value, total)}`}
                              style={{"--row-index": index + 1}}
                            >
                              {value}
                            </span>
                          );
                        })}
  
                        {/* Missing row */}
                        <span className="method-label" style={{"--row-index": 4}}>missing:</span>
                        {['15min', '2hr', '6hr'].map((timeframe, index) => {
                          const value = entityResultsMetrics?.[timeframe]?.missing || 0;
                          const total = entityResultsMetrics?.[timeframe]?.total || 1;
                          return (
                            <span
                              key={`missing-${entity}-${network}-${timeframe}`}
                              className={`percentage ${getMetricClass(value, total)}`}
                              style={{"--row-index": index + 5}}
                            >
                              {value}
                            </span>
                          );
                        })}
  
                        {/* Invalid row */}
                        <span className="method-label" style={{"--row-index": 8}}>invalid:</span>
                        {['15min', '2hr', '6hr'].map((timeframe, index) => {
                          const value = entityResultsMetrics?.[timeframe]?.invalid || 0;
                          const total = entityResultsMetrics?.[timeframe]?.total || 1;
                          return (
                            <span
                              key={`invalid-${entity}-${network}-${timeframe}`}
                              className={`percentage ${getMetricClass(value, total)}`}
                              style={{"--row-index": index + 9}}
                            >
                              {value}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

export default DataSummary;
